import {StatusContextValue, useContextStatus} from '../common/ContextStatus';
import {useOktaAuth} from '@okta/okta-react';
import {DeviceIdentity, DeviceIdentityApi} from "./DeviceIdentityApi";
import {isFirmwareGreaterThanOrEqualTo} from "./FirmwareComparison";
import { DeviceTelemetry, DeviceTelemetryApi } from './DeviceTelemetryApi';
import {saveCanProfiles} from "../CanProfile/CanProfileProvider";
import {saveModbusProfile} from "../ModbusProfile/ModbusProfileProvider";
import {saveDesiredState} from "../InputNameAndOffset/InputNameAndOffsetProvider";
import { DeviceTypeEnum, hasCanSupport } from './DeviceTypeEnum';
import { useDeviceCounters } from './useDeviceCounters';
import { AssetApi } from '../Asset/AssetApi';
import { saveOperatingMode } from '../OperatingMode/OperatingModeProvider';
import { useAnalytics } from 'components/analytics/analytics';
import { AnalyticsEvents } from 'components/analytics/analyticsEvents';
import React from 'react';
import { DeviceState, DeviceStateApi, DeviceStateDocumentation, OperatingModeType } from "./DeviceStateApi";

type UnitInfoContextValue = StatusContextValue & {
  state: DeviceState;
  setState: (deviceState: DeviceState) => void;

  stateDocumentation: DeviceStateDocumentation;
  setStateDocumentation: (deviceStateDocumentation: DeviceStateDocumentation) => void;

  identity: DeviceIdentity;
  setIdentity: (deviceIdentity: DeviceIdentity) => void;

  telemetry: DeviceTelemetry;
  setTelemetry: (deviceTelemetry: DeviceTelemetry) => void;

  can1ProfileId?: string;
  setCan1ProfileId: (canProfileId?: string) => void;
  
  originalCan1ProfileId?: string;
  setOriginalCan1ProfileId: (canProfileId?: string) => void;

  can2ProfileId?: string;
  setCan2ProfileId: (can2ProfileId?: string) => void;

  modbusProfileId?: string;
  setModbusProfileId: (modbusProfileId?: string) => void;

  operatingMode?: OperatingModeType;
  setOperatingMode: (operatingMode?: OperatingModeType) => void;

  hasTlvSupport: boolean;
  setHasTlvSupport: (hasTlvSupport: boolean) => void;

  input1Name?: string;
  setInput1Name: (input1Name?: string) => void;

  input2Name?: string;
  setInput2Name: (input2Name?: string) => void;

  input3Name?: string;
  setInput3Name: (input3Name?: string) => void;

  input4Name?: string;
  setInput4Name: (input4Name?: string) => void;

  input5Name?: string;
  setInput5Name: (input5Name?: string) => void;

  input6Name?: string;
  setInput6Name: (input6Name?: string) => void;

  totalKm?: number;
  setTotalKm: (totalKm?: number) => void;

  distanceOffset?: number;
  setDistanceOffset: (distanceOffset?: number) => void;

  kmInitialValue?: number;
  setKmInitialValue: (km?: number) => void;

  totalRun1?: number;
  setTotalRun1: (totalRun1?: number) => void;

  hoursOffset?: number;
  setHoursOffset: (hoursOffset?: number) => void;

  run1InitialValue?: number;
  setRun1InitialValue: (run1?: number) => void;

  run1?: number;
  setRun1: (run1?: number) => void;

  deviceTypeSupportsOffsets?: boolean;
  setDeviceTypeSupportsOffsets: (deviceTypeSupportsOffsets: boolean) => void;

  initializeCounters: (telemetry: DeviceTelemetry, state: DeviceState, deviceType: DeviceTypeEnum) => void;
  updateOffsetsFromState: (state: DeviceState) => void;
  getCalculatedOffsets: () => { distanceOffset?: number; hoursOffset?: number }

  note: string
  setNote: (value: string) => void

  name: string
  setName: (value: string) => void

  referenceNumber: string
  setReferenceNumber: (value: string) => void
};

const UnitInfoContext = React.createContext<UnitInfoContextValue | undefined>(
  undefined
);

type UnitInfoProviderProps = {
  children: React.ReactNode;
};

const TelematicsDeviceProvider = (props: UnitInfoProviderProps) => {
  const [contextStatus, setContextStatus] = React.useState({
    loading: false,
    saving: false,
    hasContent: false
  });
  const [deviceState, setDeviceState] = React.useState();
  const [deviceStateDocumentation, setDeviceStateDocumentation] = React.useState();
  const [deviceIdentity, setDeviceIdentity] = React.useState();
  const [deviceTelemetry, setDeviceTelemetry] = React.useState();
  const [originalCan1ProfileId, setOriginalCan1ProfileId] = React.useState(); 
  const [can1ProfileId, setCan1ProfileId] = React.useState();
  const [can2ProfileId, setCan2ProfileId] = React.useState();
  const [modbusProfileId, setModbusProfileId] = React.useState();
  const [hasTlvSupport, setHasTlvSupport] = React.useState<boolean>(false);
  const [operatingMode, setOperatingMode] = React.useState<OperatingModeType | undefined>(undefined);

  const [input1Name, setInput1Name] = React.useState();
  const [input2Name, setInput2Name] = React.useState();
  const [input3Name, setInput3Name] = React.useState();
  const [input4Name, setInput4Name] = React.useState();
  const [input5Name, setInput5Name] = React.useState();
  const [input6Name, setInput6Name] = React.useState();

  const deviceCounters = useDeviceCounters();

  const [note, setNote] = React.useState<string>('');
  const [name, setName] = React.useState<string>('');
  const [referenceNumber, setReferenceNumber] = React.useState<string>('');

  const value: UnitInfoContextValue = React.useMemo(
    () => ({
      contextStatus,
      setContextStatus,
      state: deviceState,
      setState: setDeviceState,
      stateDocumentation: deviceStateDocumentation,
      setStateDocumentation: setDeviceStateDocumentation,
      identity: deviceIdentity,
      setIdentity: setDeviceIdentity,
      telemetry: deviceTelemetry,
      setTelemetry: setDeviceTelemetry,
      originalCan1ProfileId,
      setOriginalCan1ProfileId,
      can1ProfileId,
      setCan1ProfileId,
      can2ProfileId,
      setCan2ProfileId,
      modbusProfileId,
      setModbusProfileId,
      operatingMode,
      setOperatingMode,
      hasTlvSupport,
      setHasTlvSupport,
      input1Name,
      setInput1Name,
      input2Name,
      setInput2Name,
      input3Name,
      setInput3Name,
      input4Name,
      setInput4Name,
      input5Name,
      setInput5Name,
      input6Name,
      setInput6Name,
      ...deviceCounters,
      note,
      setNote,
      name,
      setName,
      referenceNumber,
      setReferenceNumber,
    }),
    [contextStatus, deviceState, deviceStateDocumentation, deviceIdentity, deviceTelemetry, can1ProfileId, originalCan1ProfileId, can2ProfileId, modbusProfileId, operatingMode, hasTlvSupport, setHasTlvSupport, input1Name, setInput1Name, input2Name, setInput2Name, input3Name, setInput3Name, input4Name, setInput4Name, input5Name, setInput5Name, input6Name, setInput6Name,
      deviceCounters, note, setNote,
      name, setName, referenceNumber, setReferenceNumber,
    ]
  );
  return <UnitInfoContext.Provider value={value} {...props} />;
};

const useDeviceContext = () => {
  const context = React.useContext(UnitInfoContext);
  const { authState } = useOktaAuth();
  const analytics = useAnalytics();
  if (!context) {
    throw new Error(
      'useUserInfoContext must be used within a UserInfoProvider'
    );
  }

  const status = useContextStatus(context);

  const {
    contextStatus,
    state,
    setState,
    stateDocumentation,
    setStateDocumentation,
    identity,
    setIdentity,
    telemetry,
    setTelemetry,
    originalCan1ProfileId,
    setOriginalCan1ProfileId,
    can1ProfileId,
    setCan1ProfileId,
    can2ProfileId,
    setCan2ProfileId,
    modbusProfileId,
    setModbusProfileId,
    operatingMode,
    setOperatingMode,
    hasTlvSupport,
    setHasTlvSupport,
    input1Name,
    setInput1Name,
    input2Name,
    setInput2Name,
    input3Name,
    setInput3Name,
    input4Name,
    setInput4Name,
    input5Name,
    setInput5Name,
    input6Name,
    setInput6Name,
    totalKm,
    setTotalKm,
    distanceOffset,
    setDistanceOffset,
    kmInitialValue,
    setKmInitialValue,
    totalRun1,
    setTotalRun1,
    hoursOffset,
    setHoursOffset,
    run1InitialValue,
    setRun1InitialValue,
    run1,
    setRun1,
    deviceTypeSupportsOffsets,
    setDeviceTypeSupportsOffsets,
    initializeCounters,
    updateOffsetsFromState,
    getCalculatedOffsets,
    note,
    setNote,
    name,
    setName,
    referenceNumber,
    setReferenceNumber,
  } = context;

  function load(
    serialNumber: string
  ) : Promise<DeviceIdentity> {
    status.loadingStarted();
    setCan1ProfileId(undefined);
    setCan2ProfileId(undefined);
    setModbusProfileId(undefined);
    setOperatingMode(undefined);

    return DeviceIdentityApi.getIdentity(authState?.accessToken, serialNumber)
      .then(identity => {
        setIdentity(identity);

        const telemetryPromise = DeviceTelemetryApi.getTelemetry(authState?.accessToken, identity.id);
        const statePromise = loadState(identity);
        const stateDocumentationPromise = DeviceStateApi.getStateDocumentation(authState?.accessToken, identity.id);
        
        // Asset API is the source of truth for name, reference number, and note
        const assetInfoPromise = AssetApi.getAsset(authState?.accessToken, identity.assetId)
            .catch(() => ({ name: '', referenceNumber: '' })); // Fallback if Asset API fails
        // Note is fully Iris-hosted - no fallback to Classic API
        const assetNotePromise = AssetApi.getNote(authState?.accessToken, identity.assetId)
            .then(note => note || '')
            .catch(() => '')

        return Promise.all([telemetryPromise, statePromise, stateDocumentationPromise, assetNotePromise, assetInfoPromise])
          .then(([telemetry, state, stateDocumentation, assetNote, assetInfo]) => {
            setTelemetry(telemetry);
            setInput1Name(state.reported.io?.input1?.name)
            setInput2Name(state.reported.io?.input2?.name)
            setInput3Name(state.reported.io?.input3?.name)
            setInput4Name(state.reported.io?.input4?.name)
            setInput5Name(state.reported.io?.input5?.name)
            setInput6Name(state.reported.io?.input6?.name)
            
            initializeCounters(telemetry, state, identity.deviceType);
            // Asset API is the source of truth for name, reference number, and note
            setName(assetInfo.name || '');
            setReferenceNumber(assetInfo.referenceNumber || '');
            setNote(assetNote || '');
            setStateDocumentation(stateDocumentation);

            if( !state.reported.firmware?.version) {
              setHasTlvSupport(false);
            }
            else {
              setHasTlvSupport(
                identity.deviceType === DeviceTypeEnum.TU700
                || identity.deviceType === DeviceTypeEnum.GRIFFIN
                || (
                  isFirmwareGreaterThanOrEqualTo(state.reported.firmware.version, 63, 8)
                  && (identity.deviceType === DeviceTypeEnum.TU500
                    || identity.deviceType === DeviceTypeEnum.TU501
                    || identity.deviceType === DeviceTypeEnum.TU600)))
            }
            const canProfile1 = isGriffinDevice(identity) ? state.desired.canbus?.can1?.profile?.id : state.desired.canbus?.profile?.id;
            setCan1ProfileId(canProfile1);
            setOriginalCan1ProfileId(canProfile1);
            setCan2ProfileId(state.desired.canbus?.can2?.profile?.id);
            setModbusProfileId(state.desired.modbus?.profile?.id);
            setOperatingMode(state.desired.operatingMode?.mode);
            status.succeed();

            return identity;
          });
      })
      .catch(err => {
        status.failed(err.message);
        return Promise.reject();
      });
  }

  const loadState = (deviceIdentity: DeviceIdentity) => {
    return DeviceStateApi.getState(authState?.accessToken, deviceIdentity.id)
      .then(stateResult => {
        setState(stateResult);
        updateOffsetsFromState(stateResult);
        return Promise.resolve(stateResult);
      })
      .catch(err => {
        throw new Error("Fetch of state failed");
      });
  }

  const reloadState = () => {
    return loadState(identity)
  }

  const isGriffinDevice = (deviceIdentity: DeviceIdentity) => {
    const deviceType = deviceIdentity.deviceType;
    return deviceType === DeviceTypeEnum.TU700 || deviceType === DeviceTypeEnum.GRIFFIN;
  };

  const isRawDevice = (deviceIdentity: DeviceIdentity) => {
    const deviceType = deviceIdentity.deviceType;
    return isGriffinDevice(deviceIdentity) || deviceType === DeviceTypeEnum.TU500 || deviceType === DeviceTypeEnum.TU501 || deviceType === DeviceTypeEnum.TU600;
  };

  const save = () => {
    status.savingStarted();

    let devicePromises = [];
    if (hasCanSupport(identity.deviceType)) {
      devicePromises.push(saveCanProfiles(authState, can1ProfileId, can2ProfileId, identity.id, state, isGriffinDevice(identity)));
    }

    if(isGriffinDevice(identity)) {
      devicePromises.push(saveModbusProfile(authState, modbusProfileId, identity.id, state))
    }

    const { distanceOffset: calculatedDistanceOffset, hoursOffset: calculatedHoursOffset } = getCalculatedOffsets();

    devicePromises.push(saveDesiredState(authState, identity.id, state.desired, input1Name, input2Name, input3Name, input4Name, input5Name, input6Name, calculatedHoursOffset, calculatedDistanceOffset, deviceTypeSupportsOffsets));
    
    if( isRawDevice(identity) && operatingMode !== undefined && state.desired.operatingMode?.mode !== operatingMode) {
      const promise = saveOperatingMode(authState, identity.id, operatingMode)
        .then(() => {
          analytics.logEvent(AnalyticsEvents.OperatingModeSaved, { serialNumber: identity.serialNumber, mode: operatingMode });
        });
      devicePromises.push(promise);
    }

    const assetPromise = Promise.all([
      AssetApi.updateAsset(authState?.accessToken, identity.assetId, name, referenceNumber),
      AssetApi.updateNote(authState?.accessToken, identity.assetId, note)
    ]).then(() => ({ assetFailed: false }))
     .catch(() => ({ assetFailed: true }));

    return Promise.all([Promise.all(devicePromises), assetPromise])
      .then(([, assetResult]) => {
        if (assetResult.assetFailed) {
          status.succeed('Device settings were saved. Unit name, reference number and note could not be saved.');
        } else {
          status.succeed('Unit was updated');
        }
        loadState(identity);
      })
      .catch(err => status.failed(err.message, 'Could not save'));
  };

  const saveOperatingHoursOffset = (newHoursOffset: number) => {
    status.savingStarted();
    // Ignore all other parameters and only save the operating hours
    saveDesiredState(authState, identity.id, state.desired, undefined, undefined, undefined, undefined, undefined, undefined, newHoursOffset, undefined, deviceTypeSupportsOffsets)
    .then(() => {
      status.succeed('Operating hours were updated');
      loadState(identity);
    })
    .catch(err => {
      status.failed(err.message, 'Could not save Operating hours');
    });
  }



  return {
    identity,
    state,
    stateDocumentation,
    telemetry,
    hasTlvSupport,
    can1ProfileId,
    can2ProfileId,
    lastSavedCanProfileId: originalCan1ProfileId,

    load,
    reloadState,
    save,
    saveOperatingHoursOffset,

    setCan1ProfileId,
    setCan2ProfileId,
    setModbusProfileId,
    operatingMode,
    setOperatingMode,
    input1Name,
    input2Name,
    input3Name,
    input4Name,
    input5Name,
    input6Name,
    setInput1Name,
    setInput2Name,
    setInput3Name,
    setInput4Name,
    setInput5Name,
    setInput6Name,
    isGriffinDevice,
    isRawDevice,
    totalKm,
    setTotalKm,
    distanceOffset,
    setDistanceOffset,
    kmInitialValue,
    setKmInitialValue,
    totalRun1,
    setTotalRun1,
    hoursOffset,
    setHoursOffset,
    run1InitialValue,
    setRun1InitialValue,
    run1,
    deviceTypeSupportsOffsets,
    note,
    setNote,
    name,
    setName,
    referenceNumber,
    setReferenceNumber,

    contextStatus,
    dismissError: status.dismissError,
    dismissSuccess: status.dismissSuccess
  };
};

export { TelematicsDeviceProvider, useDeviceContext };
